import { useState, useEffect, useRef } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import { Icon } from '@iconify/react';
import { toast } from 'src/components/snackbar';
import axios from 'axios';
import { CONFIG } from 'src/config-global';
import EncryptUtils from 'src/utils/EncryptUtils';

interface ImageUploadPreviewProps {
  value?: string; // Current image data (JSON string with FileContentId and BlobName, or URL)
  onChange: (value: string) => void;
  label?: string;
  helperText?: string;
  disabled?: boolean;
  accept?: string;
  onUploadStateChange?: (isUploading: boolean) => void; // Callback to notify parent of upload state
  onUploadReady?: (uploadFn: () => Promise<string | null>) => void; // Callback to provide upload function for form submission
}

// Helper function to fetch image blob
async function fetchImageBlob(fileContentId: number, blobName: string): Promise<Blob> {
  const body = {
    tk: EncryptUtils.encryptedData({ FileContentID: fileContentId, blobName }),
  };
  const res = await axios.post(`${CONFIG.baseUrl}/property/get-images`, body, {
    responseType: 'blob',
  });
  return res.data as Blob;
}

export function ImageUploadPreview({
  value,
  onChange,
  label,
  helperText,
  disabled = false,
  accept = 'image/*',
  onUploadStateChange,
  onUploadReady,
}: ImageUploadPreviewProps) {
  const [preview, setPreview] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Notify parent of upload state changes
  useEffect(() => {
    onUploadStateChange?.(uploading);
  }, [uploading, onUploadStateChange]);


  // Load preview from value (could be URL or JSON with FileContentId/BlobName)
  // Skip loading if value is pending upload marker (we already have local preview)
  useEffect(() => {
    const loadPreview = async () => {
      // If value is pending upload marker, keep the existing preview (from local file)
      if (value === '__PENDING_UPLOAD__') {
        return; // Keep the local blob preview, don't load anything
      }

      if (!value) {
        // Only clear preview if we don't have a local blob URL
        if (!preview || !preview.startsWith('blob:')) {
          setPreview(null);
        }
        return;
      }

      // If we already have a preview from a local file upload, don't reload
      if (preview && preview.startsWith('blob:') && pendingFile) {
        // We have a pending file, keep the local preview
        return;
      }

      // If value is a URL (starts with http or /), use it directly
      if (value.startsWith('http') || value.startsWith('/')) {
        // Clean up old blob URL if exists
        if (preview && preview.startsWith('blob:')) {
          URL.revokeObjectURL(preview);
        }
        setPreview(value);
        return;
      }

      // Otherwise, try to parse as JSON (FileContentId and BlobName)
      try {
        const imageData = JSON.parse(value);
        if (imageData.FileContentId && imageData.BlobName) {
          // Only fetch from API if we don't already have a preview
          // This prevents calling API immediately after upload
          if (!preview || !preview.startsWith('blob:')) {
            try {
              const blob = await fetchImageBlob(imageData.FileContentId, imageData.BlobName);
              const blobUrl = URL.createObjectURL(blob);
              // Clean up old blob URL if exists
              if (preview && preview.startsWith('blob:')) {
                URL.revokeObjectURL(preview);
              }
              setPreview(blobUrl);
            } catch (err) {
              console.error('Error loading image:', err);
              setPreview(null);
            }
          }
        } else if (!preview || !preview.startsWith('blob:')) {
          setPreview(null);
        }
      } catch {
        // If not JSON, treat as URL
        if (preview && preview.startsWith('blob:')) {
          URL.revokeObjectURL(preview);
        }
        setPreview(value);
      }
    };

    loadPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]); // Intentionally excluding 'preview' and 'pendingFile' to prevent infinite loops

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Validate file size (max 10MB)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      toast.error('Image size must be less than 10MB');
      return;
    }

    // Show preview immediately using local file (NO UPLOAD - just preview)
    const localUrl = URL.createObjectURL(file);
    setPreview(localUrl);
    
    // Store the file object - we'll upload it when form is submitted
    setPendingFile(file);
    
    // Store a special marker to indicate file is selected but not uploaded yet
    // This will be replaced with actual upload result when form is submitted
    onChange('__PENDING_UPLOAD__');
    
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Expose upload function for form submission
  const uploadPendingFile = async (): Promise<string | null> => {
    if (!pendingFile) {
      // If no pending file, check if value is already uploaded (JSON format)
      if (value && value !== '__PENDING_UPLOAD__' && !value.startsWith('data:') && !value.startsWith('http') && !value.startsWith('/')) {
        try {
          JSON.parse(value); // If it's valid JSON, it's already uploaded
          return value;
        } catch {
          // Not JSON, might be URL
          return value;
        }
      }
      return value || null;
    }

    setUploading(true);
    try {
      // Upload to blob storage
      const formData = new FormData();
      formData.append('files', pendingFile);
      formData.append('payload', JSON.stringify({}));

      const containerName = 'parkcity-source-files';
      const response = await axios.post(
        `${CONFIG.baseUrl}/file/upload_file_to_storage/${containerName}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      if (response.data?.results?.[0]) {
        const { FileContentId, file: BlobName } = response.data.results[0];
        const imageData = JSON.stringify({ FileContentId, BlobName });
        
        // Clear pending file and update value
        setPendingFile(null);
        onChange(imageData);
        
        return imageData;
      }
      throw new Error('Upload failed - no file data returned');
    } catch (err: any) {
      console.error('Error uploading image:', err);
      throw new Error(err?.response?.data?.message || 'Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  // Expose upload function to parent when it changes
  useEffect(() => {
    if (onUploadReady) {
      onUploadReady(uploadPendingFile);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingFile]); // Only depend on pendingFile - uploadPendingFile is stable

  const handleRemove = () => {
    if (preview && preview.startsWith('blob:')) {
      URL.revokeObjectURL(preview);
    }
    setPreview(null);
    onChange('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleClick = () => {
    if (!disabled && !uploading) {
      fileInputRef.current?.click();
    }
  };

  return (
    <Box>
      {label && (
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          {label}
        </Typography>
      )}

      <Stack spacing={2}>
        {/* Preview Area */}
        {preview ? (
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              maxWidth: 400,
              aspectRatio: '16/9',
              borderRadius: 2,
              overflow: 'hidden',
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.neutral',
            }}
          >
            <Box
              component="img"
              src={preview}
              alt="Preview"
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
            />
            {!disabled && (
              <Button
                size="small"
                color="error"
                variant="contained"
                onClick={handleRemove}
                disabled={uploading}
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  minWidth: 'auto',
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  p: 0,
                }}
              >
                <Icon icon="eva:close-fill" width={20} />
              </Button>
            )}
          </Box>
        ) : (
          <Box
            onClick={handleClick}
            sx={{
              width: '100%',
              maxWidth: 400,
              aspectRatio: '16/9',
              borderRadius: 2,
              border: '2px dashed',
              borderColor: 'divider',
              bgcolor: 'background.neutral',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: disabled || uploading ? 'not-allowed' : 'pointer',
              opacity: disabled || uploading ? 0.6 : 1,
              '&:hover': {
                borderColor: 'primary.main',
                bgcolor: 'action.hover',
              },
            }}
          >
            <Stack spacing={1} alignItems="center">
              {uploading ? (
                <>
                  <Icon icon="eos-icons:loading" width={48} />
                  <Typography variant="caption">Uploading...</Typography>
                </>
              ) : (
                <>
                  <Icon icon="eva:cloud-upload-fill" width={48} color="#637381" />
                  <Typography variant="caption" color="text.secondary">
                    Click to upload image
                  </Typography>
                </>
              )}
            </Stack>
          </Box>
        )}

        {/* Upload Button */}
        {!preview && !uploading && (
          <Button
            variant="outlined"
            onClick={handleClick}
            disabled={disabled}
            startIcon={<Icon icon="eva:upload-fill" />}
            sx={{ alignSelf: 'flex-start' }}
          >
            Choose Image
          </Button>
        )}

        {/* Hidden File Input */}
        <input
          ref={fileInputRef}
          type="file"
          accept={accept}
          onChange={handleFileSelect}
          style={{ display: 'none' }}
          disabled={disabled || uploading}
        />

        {helperText && (
          <Typography variant="caption" color="text.secondary">
            {helperText}
          </Typography>
        )}
      </Stack>
    </Box>
  );
}

